import { useForm } from 'react-hook-form';
import { ApiCustomerIndividual } from '../../../openapi';
import { FormContentIsland, FormTextField, Grid, HookForm, Button } from '@razz-team/ui-web';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormError } from '../../../hooks/useFormError';
import { useUpdateCustomerLegalInfoMutation } from '../../../api/customers';

type FormValues = {
  firstName: string;
  lastName: string;
  middleName: string;
};

export function UpdateNameForm({
  customer,
  onComplete,
}: {
  customer: ApiCustomerIndividual;
  onComplete?: () => void;
}) {
  const { t } = useTranslation();
  const updateMutation = useUpdateCustomerLegalInfoMutation();

  useEffect(() => {
    if (updateMutation.isSuccess) {
      onComplete?.();
    }
  }, [updateMutation.isSuccess, onComplete]);

  const form = useForm<FormValues>({
    defaultValues: {
      firstName: customer.name?.firstName ?? undefined,
      lastName: customer.name?.lastName ?? undefined,
      middleName: customer.name?.middleName ?? undefined,
    },
  });

  useFormError(form, 'firstName', updateMutation.error);

  const handleSubmit = useCallback(
    (values: FormValues) => {
      updateMutation.mutate({
        legalName: {
          firstName: values.firstName,
          lastName: values.lastName,
          middleName: values.middleName,
        },
      });
    },
    [updateMutation],
  );

  return (
    <Grid
      flexDirection="column"
      gap={4}
      component={HookForm}
      form={form}
      onSubmit={handleSubmit}
      size={12}
    >
      <FormContentIsland>
        <FormTextField
          id="firstName"
          name="firstName"
          label={t('First name')}
          fullWidth
          required
          autoComplete="given-name"
          autoFocus
        />
        <FormTextField
          id="lastName"
          name="lastName"
          label={t('Last name')}
          fullWidth
          required
          autoComplete="family-name"
        />
        <FormTextField
          id="middleName"
          name="middleName"
          label={t('Middle name (optional)')}
          fullWidth
          autoComplete="additional-name"
        />
      </FormContentIsland>
      <Button type="submit" size="large" loading={updateMutation.isPending} fullWidth>
        {t('Save changes')}
      </Button>
    </Grid>
  );
}
